import { SelectedItem } from "01Data/02User/Locations";
import { AppContext } from "01Data/AppData";
import { Sensor } from "01Data/DataTypes";
import { ConfirmDialog } from "04UserPages/99Parts/ConfirmDialog";
import { MapReader } from "97Parts/MapReader";
import { NoteText } from "97Parts/Styles";
import { useSignal } from "99Tools/useModel";
import { Cancel, CheckCircle, Delete, OpenInNew } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  styled,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { LIST_HEADER_COLOR } from "Settings";
import { SECTION, TABLE_STYLE } from "deviceList";
import { useContext, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { CurrentSection } from "./DevicesPage";

const FormDlg = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%", // Fix IE 11 issue.
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const ButtonDlg = styled(LoadingButton)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

export const DetailFormLTE = ({ reload }: { reload: () => void }) => {
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageDevices;
  const l = p.selectedLTE.sensor;
  const [bShowConfirmDelete, setShowConfirmDelete] = useState(false);
  const redraw = useSignal();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleClose = () => {
    p.listLTE.bShowDetailDlg = false;
    l.clear();
    p.changed();
  };

  const handleDelete = () => {
    setShowConfirmDelete(false);
    setDeleteLoading(true);
    d.DeleteLTE()
      .then(() => {
        p.listLTE.bShowDetailDlg = false;
        p.LTE.splice(p.selectedLTE.index, 1);
        p.selectedLTE = new SelectedItem(); // 選択なし状態にする
        p.changed();
      })
      .catch((err: any) => {
        l.setError(err, "デバイス削除に失敗しました。");
        setShowConfirmDelete(false);
        p.changed();
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleChangeName = (e: any) => {
    p.selectedLTE.sensor.SensorName = e.target.value;
    l.errorMessage = "";
    redraw();
  };

  const handleChangeLocation = (e: any) => {
    p.selectedLTE.sensor.LocationName = e.target.value;
    l.errorMessage = "";
    redraw();
  };

  const handleApply = () => {
    if (p.selectedLTE.sensor.SensorName === "") {
      l.errorMessage = "表示名を入力してください。";
      redraw();
      return;
    }
    setLoading(true);
    d.EditLTE()
      .then(() => {
        p.listLTE.bShowDetailDlg = false;
        p.LTE[p.selectedLTE.index] = p.selectedLTE.sensor;
        reload();
      })
      .catch((err: any) => {
        l.setError(err, "デバイス設定変更に失敗しました。");
        d.pageDevices.changed();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!p.selectedLTE.IsSelected()) {
    return null;
  }

  return (
    <>
      <Dialog key="detail" open={p.listLTE.bShowDetailDlg}>
        <FormDlg noValidate>
          <DialogTitle>詳細情報編集</DialogTitle>
          <DialogContent>
            <NoteText>付帯情報</NoteText>
            <TextField
              required
              variant="filled"
              margin="normal"
              fullWidth
              id={String("表示名")}
              label={"表示名"}
              name="name"
              inputProps={{ maxLength: 128 }}
              value={p.selectedLTE.sensor.SensorName}
              onChange={handleChangeName}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              id={String("設置場所")}
              label={"設置場所"}
              name="location"
              inputProps={{ maxLength: 128 }}
              value={p.selectedLTE.sensor.LocationName}
              onChange={handleChangeLocation}
            />
            <MapReader entity={l} redraw={redraw} />
            {l.errorMessage !== "" ? (
              <ErrorText>{l.errorMessage}</ErrorText>
            ) : null}
            <NoteText>漏水みはり番削除</NoteText>
            <DialogActions>
              <ButtonDlg
                startIcon={<Delete />}
                variant="contained"
                color="secondary"
                onClick={() => setShowConfirmDelete(true)}
                loading={deleteLoading}
              >
                漏水みはり番削除
              </ButtonDlg>
            </DialogActions>
            <Divider variant="middle" />
            <DialogActions>
              <ButtonDlg
                startIcon={<CheckCircle />}
                variant="contained"
                color="primary"
                onClick={handleApply}
                loading={loading}
              >
                適用
              </ButtonDlg>
              <ButtonDlg
                startIcon={<Cancel />}
                variant="contained"
                color="primary"
                onClick={handleClose}
              >
                キャンセル
              </ButtonDlg>
            </DialogActions>
          </DialogContent>
        </FormDlg>
      </Dialog>
      <ConfirmDialog
        bShow={bShowConfirmDelete}
        title={`${p.selectedLTE.sensorPrev.SensorName}を削除しますか？`}
        description=""
        onApply={handleDelete}
        onCancel={() => setShowConfirmDelete(false)}
        titleButtonApply="削除"
        titleButtonCancel="キャンセル"
      />
    </>
  );
};

const columns = [
  {
    id: "id",
    label: "機材ID",
    minWidthPC: 100,
    minWidthSmartphone: "10vw",
  },
  { id: "name", label: "表示名", minWidthPC: 100, minWidthSmartphone: "10vw" },
  { id: "kind", label: "種別", minWidthPC: 100, minWidthSmartphone: "10vw" },
  {
    id: "location",
    label: "設置場所",
    minWidthPC: 100,
    minWidthSmartphone: "10vw",
  },
  { id: "map", label: "詳細場所", minWidthPC: 100, minWidthSmartphone: "10vw" },
];

/**
 * LTE + SubGiga 一覧
 * @returns TSX
 */
const LTEBody = (props: any) => {
  const a = useContext(AppContext).account;
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageDevices;
  const sortInfo = props.sortInfo;

  const handleDetailDlgOpen = (id: string) => {
    if (a.IsUserViewer()) {
      return;
    }
    const i = p.LTE.findIndex((e) => e.SensorId === id);
    p.listLTE.bShowDetailDlg = true;
    p.selectedLTE = new SelectedItem(i, p.LTE[i]);
    p.changed();
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const sortByOrder = (a: string, b: string, order: string) => {
    return order === "asc"
      ? a.localeCompare(b, "ja")
      : b.localeCompare(a, "ja");
  };

  const updateSort = (a: Sensor, b: Sensor) => {
    switch (sortInfo.target) {
      case "機材ID":
        return sortByOrder(a.SensorId, b.SensorId, sortInfo.order);
      case "表示名":
        return sortByOrder(a.SensorName, b.SensorName, sortInfo.order);
      case "設置場所":
        return sortByOrder(a.LocationName, b.LocationName, sortInfo.order);
      default:
        return 0;
    }
  };

  const rows = p.LTE.slice(
    p.listLTE.page * p.listLTE.rowsPerPage,
    p.listLTE.page * p.listLTE.rowsPerPage + p.listLTE.rowsPerPage
  )
    .sort((a, b) => updateSort(a, b))
    .map((row: Sensor, i: number) => {
      return (
        <TableRow hover role="checkbox" tabIndex={-1} key={row.SensorId}>
          <TableCell
            key={columns[0].id + i}
            align="center"
            sx={a.IsUserViewer() ? null : { cursor: "pointer" }}
            onClick={() => handleDetailDlgOpen(row.SensorId)}
          >
            {row.SensorId.toString()}
          </TableCell>
          <TableCell
            key={columns[1].id + i}
            align="center"
            sx={a.IsUserViewer() ? null : { cursor: "pointer" }}
            onClick={() => handleDetailDlgOpen(row.SensorId)}
          >
            {row.SensorName}
          </TableCell>
          <TableCell key={columns[2].id + i} align="center">
            {row.SensorType}
          </TableCell>
          <TableCell
            key={columns[3].id + i}
            align="center"
            sx={a.IsUserViewer() ? null : { cursor: "pointer" }}
            onClick={() => handleDetailDlgOpen(row.SensorId)}
          >
            {row.LocationName}
          </TableCell>
          <TableCell key={columns[4].id + i} align="center">
            {row.SensorImage === "" ? (
              <Button
                size="small"
                startIcon={<OpenInNew />}
                variant="contained"
                disabled
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            ) : (
              <Button
                size="small"
                startIcon={<OpenInNew />}
                variant="contained"
                onClick={() => handleShowMap(row.SensorImage)}
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            )}
          </TableCell>
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

/**
 * LTEテーブル
 * @returns TSX
 */
const LTETable = () => {
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pageDevices;
  const section = useContext(CurrentSection);
  const sx =
    section === SECTION.ALL
      ? TABLE_STYLE.TOW_SECTIONS
      : TABLE_STYLE.ONE_SECTION;
  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    p.listLTE.page = newPage;
    p.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    p.listLTE.rowsPerPage = +event.target.value;
    p.listLTE.page = 0;
    p.changed();
  };

  const [sortInfo, setSortInfo] = useState({ target: "表示名", order: "asc" });
  const handleChangeSortOrder = (target: any) => {
    if (target !== columns[0].label && target !== columns[1].label) {
      return;
    }
    if (target !== sortInfo.target) {
      setSortInfo({ target: target, order: "asc" });
    } else {
      if (sortInfo.order === "asc") {
        setSortInfo({ target: target, order: "desc" });
      } else {
        setSortInfo({ target: target, order: "asc" });
      }
    }
  };

  const GetSortExp = (label: string, order: string) => {
    if (label !== sortInfo.target) {
      return "";
    }
    switch (order) {
      case "asc":
        return "▲";
      case "desc":
        return "▼";
      default:
        return "";
    }
  };

  const header = columns.map((column, index) => (
    <ColorHeader
      key={column.id}
      align="center"
      style={{
        minWidth: isSmartphone ? column.minWidthSmartphone : column.minWidthPC,
        cursor: index < 2 ? "pointer" : "default",
      }}
      onClick={() => handleChangeSortOrder(column.label)}
    >
      {column.label + GetSortExp(column.label, sortInfo.order)}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={sx}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <LTEBody sortInfo={sortInfo} />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={p.LTE.length}
        rowsPerPage={p.listLTE.rowsPerPage}
        page={p.listLTE.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default LTETable;
